import { useCallback, useMemo } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from '../context/UserContext';
import { useAppData } from '../context/AppDataContext';
import { saveRealEstateProperty } from '../lib/saveRealEstateProperty';
import { newId } from '../utils/id';
import type { TaskItem, RealEstateProjectTask } from '../types';

/** A project task that's been marked to show in the global task list. */
export type LinkedProjectTask = RealEstateProjectTask & {
  propertyId: string;
  propertyName: string;
  phaseName?: string;
};

/**
 * Simple personal task tracker. The whole list lives as a `tasks` array field
 * on `users/{uid}` (array order = user-chosen sort order), streamed through the
 * existing user-doc snapshot in `useUserProfile`, so writes here show up
 * instantly via Firestore latency compensation.
 *
 * Also includes project tasks from real estate items when `addToGlobalTasks` is true.
 */
export function useTasks(): {
  tasks: TaskItem[];
  projectTasks: LinkedProjectTask[];
  loading: boolean;
  addTask: (title: string) => void;
  toggleTask: (id: string) => void;
  deleteTask: (id: string) => void;
  reorderTasks: (ids: string[]) => void;
  toggleProjectTask: (taskId: string, propertyId: string) => void;
} {
  const { user, tasks, loading } = useUser();
  const { realEstateItems } = useAppData();
  const uid = user?.uid;
  const list = useMemo(() => tasks ?? [], [tasks]);

  const projectTasks = useMemo<LinkedProjectTask[]>(() => {
    if (!realEstateItems) return [];
    const linkedTasks: LinkedProjectTask[] = [];
    for (const property of realEstateItems) {
      if (!property.projectTasks) continue;
      for (const task of property.projectTasks) {
        if (!task.addToGlobalTasks) continue;
        const phase = property.projectPhases?.find((p) => p.id === task.phaseId);
        linkedTasks.push({
          ...task,
          propertyId: property.id,
          propertyName: property.name,
          phaseName: phase?.name,
        });
      }
    }
    return linkedTasks.sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [realEstateItems]);

  const persist = useCallback(
    (next: TaskItem[]) => {
      if (!uid) return;
      setDoc(doc(db, 'users', uid), { tasks: next }, { merge: true }).catch((error) => {
        console.error('Failed to persist tasks:', error);
      });
    },
    [uid],
  );

  const addTask = useCallback(
    (title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      const task: TaskItem = {
        id: newId(),
        title: trimmed,
        done: false,
        createdAt: new Date().toISOString(),
        completedAt: null,
      };
      persist([...list, task]);
    },
    [list, persist],
  );

  const toggleTask = useCallback(
    (id: string) => {
      persist(
        list.map((task) =>
          task.id === id
            ? {
                ...task,
                done: !task.done,
                completedAt: task.done ? null : new Date().toISOString(),
              }
            : task,
        ),
      );
    },
    [list, persist],
  );

  const deleteTask = useCallback(
    (id: string) => {
      persist(list.filter((task) => task.id !== id));
    },
    [list, persist],
  );

  /** Reorders open tasks to match `ids`; unknown ids are ignored, missing ones keep their place at the end. */
  const reorderTasks = useCallback(
    (ids: string[]) => {
      const byId = new Map(list.map((task) => [task.id, task]));
      const next: TaskItem[] = [];
      const seen = new Set<string>();
      for (const id of ids) {
        const task = byId.get(id);
        if (task && !seen.has(id)) {
          next.push(task);
          seen.add(id);
        }
      }
      for (const task of list) {
        if (!seen.has(task.id)) next.push(task);
      }
      persist(next);
    },
    [list, persist],
  );

  const toggleProjectTask = useCallback(
    async (taskId: string, propertyId: string) => {
      if (!uid || !realEstateItems) return;
      const property = realEstateItems.find((p) => p.id === propertyId);
      if (!property || !property.projectTasks) return;

      const task = property.projectTasks.find((t) => t.id === taskId);
      if (!task) return;

      const updatedTask: RealEstateProjectTask = {
        ...task,
        done: !task.done,
        completedAt: task.done ? null : new Date().toISOString(),
      };

      const updatedTasks = property.projectTasks.map((t) =>
        t.id === taskId ? updatedTask : t
      );

      try {
        await saveRealEstateProperty(uid, {
          ...property,
          id: property.id,
          projectPhases: property.projectPhases,
          projectTasks: updatedTasks,
          existing: {
            createdAt: property.createdAt,
            projectPhases: property.projectPhases,
            projectTasks: updatedTasks,
          },
        });
      } catch (error) {
        console.error('Failed to toggle project task:', error);
      }
    },
    [uid, realEstateItems],
  );

  return {
    tasks: list,
    projectTasks,
    loading,
    addTask,
    toggleTask,
    deleteTask,
    reorderTasks,
    toggleProjectTask,
  };
}
